// components/harness/HarnessPlayground.jsx
/**
 * Harness Playground — a live demo that the web app IS a harness.
 *
 * Runs a real (minimal) agent harness in the browser against the public API
 * with the signed-in user's own aridel_live_* key: the exact OpenAI wire
 * protocol OpenClaw / Cline / Aider speak. Two demos:
 *
 *   1. Agent tool loop — sequential requests; the model calls workspace tools,
 *      the browser executes them, results go back, until finish_reason "stop".
 *   2. Parallel fan-out — one request per workspace file, all in flight at
 *      once, each independently routed; then one combine request.
 *
 * Every HTTP turn renders as a card: routed model, routing metadata, streamed
 * output, tool calls/results, token usage + cost, and the raw wire JSON.
 */

import React, { useMemo, useRef, useState } from 'react';
import {
    ArrowLeft, Play, Square, TerminalSquare, FileText, ChevronDown,
    Wrench, Braces, Copy, Check, Zap, GitFork,
} from 'lucide-react';

import { useChatSession } from '../../context/ChatSessionContext';
import { useModelCatalog } from '../../context/ModelCatalogContext';
import {
    VirtualWorkspace, SEED_FILES, runAgentLoop, runFanOut,
    OPENCLAW_CONFIG_SNIPPET,
} from '../../harness-demo';
import { RoutingExpander } from '../chat/RoutingExpander';
import ModelIcon from '../common/ModelIcon';
import styles from './HarnessPlayground.module.css';

const PRESET_TASKS = [
    {
        label: 'Fix the port',
        task: 'Read config.json and change the port to 8080. Then confirm what you changed.',
    },
    {
        label: 'Unify email validation',
        task: 'Find the email validation logic in this workspace and rewrite it into a new file validators.js with a more robust implementation. Explain what was wrong with the old one.',
    },
    {
        label: 'Sales report',
        task: 'Read sales_q3.csv, compute the month-over-month revenue growth per region, and write a short REPORT.md with the numbers and one recommendation.',
    },
];

const fmtTokens = (u) => (u ? `${u.prompt_tokens ?? 0}→${u.completion_tokens ?? 0} tok` : null);
const fmtCost = (u) => {
    const c = u?.cost_usd;
    if (typeof c !== 'number') return null;
    return c >= 0.01 ? `$${c.toFixed(3)}` : `$${c.toFixed(5)}`;
};

function JsonExpander({ label, value }) {
    const [open, setOpen] = useState(false);
    return (
        <div className={styles.jsonExpander}>
            <button type="button" className={styles.jsonToggle} onClick={() => setOpen(o => !o)}>
                <Braces size={11} />
                <span>{label}</span>
                <ChevronDown size={11} className={open ? styles.chevOpen : ''} />
            </button>
            {open && (
                <pre className={styles.jsonPre}>{JSON.stringify(value, null, 2)}</pre>
            )}
        </div>
    );
}

function ModelBadge({ slug, slugToName }) {
    if (!slug) return <span className={styles.modelBadge}><span className={styles.routingDots}>routing…</span></span>;
    const name = slugToName?.[slug] ?? slug;
    return (
        <span className={styles.modelBadge} title={`wire id: ${slug}`}>
            <ModelIcon modelName={name} size={14} />
            <span>{name}</span>
        </span>
    );
}

function TurnCard({ turn, slugToName }) {
    return (
        <div className={styles.turnCard}>
            <div className={styles.turnHeader}>
                <span className={styles.turnNo}>HTTP request #{turn.requestNo}</span>
                <ModelBadge slug={turn.model} slugToName={slugToName} />
                {turn.routing && <RoutingExpander routing={turn.routing} />}
                <span className={styles.turnMeta}>
                    {[fmtTokens(turn.usage), fmtCost(turn.usage)].filter(Boolean).join(' · ')}
                </span>
            </div>

            {turn.text && <div className={styles.turnText}>{turn.text}</div>}

            {turn.toolCalls.map((tc, i) => (
                <div key={i} className={styles.toolRow}>
                    <div className={styles.toolCall}>
                        <Wrench size={12} />
                        <code>{tc.function.name}({tc.function.arguments})</code>
                    </div>
                    {turn.toolResults[i] && (
                        <div className={styles.toolResult}>
                            <span className={styles.toolResultLabel}>→ executed locally:</span>
                            <code>{turn.toolResults[i].result.slice(0, 200)}{turn.toolResults[i].result.length > 200 ? '…' : ''}</code>
                        </div>
                    )}
                </div>
            ))}

            {turn.error && <div className={styles.turnError}>⚠ {turn.error}</div>}
            {turn.finishReason && (
                <div className={styles.finishRow}>finish_reason: <code>{turn.finishReason}</code></div>
            )}
            <JsonExpander label="wire request JSON" value={turn.requestBody} />
        </div>
    );
}

function FanCard({ card, slugToName }) {
    const isCombine = card.label === 'combine';
    return (
        <div className={`${styles.fanCard} ${isCombine ? styles.fanCombine : ''} ${card.done ? styles.fanDone : ''}`}>
            <div className={styles.fanHeader}>
                {isCombine ? <GitFork size={13} /> : <FileText size={13} />}
                <span className={styles.fanLabel}>{isCombine ? 'combine summaries' : card.label}</span>
                <ModelBadge slug={card.model} slugToName={slugToName} />
            </div>
            <div className={styles.fanText}>
                {card.error ? `⚠ ${card.error}` : (card.text || '…')}
            </div>
            <div className={styles.fanMeta}>
                {[fmtTokens(card.usage), fmtCost(card.usage)].filter(Boolean).join(' · ')}
            </div>
        </div>
    );
}

export default function HarnessPlayground() {
    const { setViewMode } = useChatSession();
    const { slugToName } = useModelCatalog();

    const [tab, setTab] = useState('loop');            // 'loop' | 'fanout'
    const [task, setTask] = useState(PRESET_TASKS[0].task);
    const [running, setRunning] = useState(false);
    const [turns, setTurns] = useState([]);
    const [fanCards, setFanCards] = useState([]);
    const [wsFiles, setWsFiles] = useState({ ...SEED_FILES });
    const [wsWritten, setWsWritten] = useState(new Set());
    const [openFile, setOpenFile] = useState(null);
    const [copied, setCopied] = useState(false);
    const abortRef = useRef(null);

    const totals = useMemo(() => {
        const records = tab === 'loop' ? turns : fanCards;
        let tokens = 0, cost = 0, requests = 0;
        for (const r of records) {
            if (r.usage) {
                tokens += r.usage.total_tokens ?? 0;
                cost += r.usage.cost_usd ?? 0;
            }
            requests += 1;
        }
        return { tokens, cost, requests };
    }, [tab, turns, fanCards]);

    const stop = () => {
        abortRef.current?.abort();
        abortRef.current = null;
        setRunning(false);
    };

    const runLoop = async () => {
        if (running || !task.trim()) return;
        setRunning(true);
        setTurns([]);
        const ws = new VirtualWorkspace();
        const abort = new AbortController();
        abortRef.current = abort;
        const sync = () => {
            setTurns(t => [...t]);
            setWsFiles({ ...ws.files });
            setWsWritten(new Set(ws.written));
        };
        try {
            await runAgentLoop(task, ws, {
                onTurnStart: (turn) => setTurns(t => [...t, turn]),
                onTurnUpdate: sync,
                onTurnEnd: sync,
            }, { signal: abort.signal });
        } catch (err) {
            if (!abort.signal.aborted) console.error('HarnessPlayground: loop failed', err);
        } finally {
            setRunning(false);
            abortRef.current = null;
        }
    };

    const runFan = async () => {
        if (running) return;
        setRunning(true);
        setFanCards([]);
        const ws = new VirtualWorkspace();
        const abort = new AbortController();
        abortRef.current = abort;
        try {
            await runFanOut(ws, { onUpdate: setFanCards }, { signal: abort.signal });
        } catch (err) {
            if (!abort.signal.aborted) console.error('HarnessPlayground: fan-out failed', err);
        } finally {
            setRunning(false);
            abortRef.current = null;
        }
    };

    const copyConfig = () => {
        navigator.clipboard?.writeText(OPENCLAW_CONFIG_SNIPPET).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1600);
        });
    };

    return (
        <div className={styles.page}>
            <header className={styles.header}>
                <button type="button" className={styles.backBtn} onClick={() => setViewMode('chat')}>
                    <ArrowLeft size={16} /> Back
                </button>
                <div className={styles.headerTitleWrap}>
                    <TerminalSquare size={18} />
                    <h1 className={styles.title}>Harness Playground</h1>
                </div>
            </header>

            <p className={styles.intro}>
                This page is a <strong>real agent harness</strong> running in your browser —
                the same OpenAI-compatible wire protocol OpenClaw, Cline and Aider speak,
                sent to <code>api.aridel.ai</code> with your own key. Nothing is simulated:
                every card below is a live HTTP request, independently routed, streamed and
                billed. Tools run locally against a small virtual workspace, exactly like a
                harness runs them on your machine.
            </p>

            <div className={styles.tabs}>
                <button
                    type="button"
                    className={`${styles.tabBtn} ${tab === 'loop' ? styles.tabActive : ''}`}
                    onClick={() => !running && setTab('loop')}
                >
                    <Wrench size={13} /> Agent tool loop
                </button>
                <button
                    type="button"
                    className={`${styles.tabBtn} ${tab === 'fanout' ? styles.tabActive : ''}`}
                    onClick={() => !running && setTab('fanout')}
                >
                    <Zap size={13} /> Parallel fan-out
                </button>
                <span className={styles.totals}>
                    {totals.requests > 0 && (
                        <>{totals.requests} requests · {totals.tokens} tokens
                        {totals.cost > 0 && <> · ${totals.cost.toFixed(4)}</>}</>
                    )}
                </span>
            </div>

            <div className={styles.body}>
                <aside className={styles.workspace}>
                    <div className={styles.wsTitle}>Virtual workspace</div>
                    {Object.keys(wsFiles).sort().map((p) => (
                        <div key={p}>
                            <button
                                type="button"
                                className={`${styles.wsFile} ${wsWritten.has(p) ? styles.wsFileWritten : ''}`}
                                onClick={() => setOpenFile(openFile === p ? null : p)}
                            >
                                <FileText size={12} />
                                <span>{p}</span>
                                {wsWritten.has(p) && <span className={styles.wsBadge}>edited</span>}
                            </button>
                            {openFile === p && <pre className={styles.wsPre}>{wsFiles[p]}</pre>}
                        </div>
                    ))}
                    <p className={styles.wsHint}>
                        In-memory only — resets on every run. Files the agent writes are
                        highlighted.
                    </p>
                </aside>

                <main className={styles.main}>
                    {tab === 'loop' ? (
                        <>
                            <div className={styles.presets}>
                                {PRESET_TASKS.map((p) => (
                                    <button
                                        key={p.label}
                                        type="button"
                                        className={`${styles.presetBtn} ${task === p.task ? styles.presetActive : ''}`}
                                        onClick={() => !running && setTask(p.task)}
                                    >
                                        {p.label}
                                    </button>
                                ))}
                            </div>
                            <div className={styles.runRow}>
                                <textarea
                                    className={styles.taskInput}
                                    value={task}
                                    onChange={(e) => setTask(e.target.value)}
                                    rows={2}
                                    disabled={running}
                                />
                                {running ? (
                                    <button type="button" className={styles.stopBtn} onClick={stop}>
                                        <Square size={14} /> Stop
                                    </button>
                                ) : (
                                    <button type="button" className={styles.runBtn} onClick={runLoop}>
                                        <Play size={14} /> Run agent
                                    </button>
                                )}
                            </div>
                            <div className={styles.timeline}>
                                {turns.map((t) => (
                                    <TurnCard key={t.requestNo} turn={t} slugToName={slugToName} />
                                ))}
                                {turns.length === 0 && !running && (
                                    <p className={styles.empty}>
                                        Pick a task and hit Run. You'll watch the loop a harness runs:
                                        the model calls tools, the browser executes them, results go
                                        back — one independent, independently-routed HTTP request per
                                        turn — until it answers with <code>finish_reason: "stop"</code>.
                                    </p>
                                )}
                            </div>
                        </>
                    ) : (
                        <>
                            <div className={styles.runRow}>
                                <p className={styles.fanIntro}>
                                    One request per workspace file, <strong>all in flight at once</strong> —
                                    Aridel routes each on its own content (code, data, prose can land on
                                    different models) — then one request to merge the results.
                                </p>
                                {running ? (
                                    <button type="button" className={styles.stopBtn} onClick={stop}>
                                        <Square size={14} /> Stop
                                    </button>
                                ) : (
                                    <button type="button" className={styles.runBtn} onClick={runFan}>
                                        <Play size={14} /> Run fan-out
                                    </button>
                                )}
                            </div>
                            <div className={styles.fanGrid}>
                                {fanCards.map((c, i) => (
                                    <FanCard key={i} card={c} slugToName={slugToName} />
                                ))}
                                {fanCards.length === 0 && !running && (
                                    <p className={styles.empty}>
                                        Hit Run to fan out. To Aridel these are just N ordinary requests
                                        arriving together — which is exactly why any harness's
                                        parallelism works with no special support.
                                    </p>
                                )}
                            </div>
                        </>
                    )}

                    <div className={styles.connectBlock}>
                        <div className={styles.connectHeader}>
                            <span>Connect a real harness — this demo is byte-for-byte this OpenClaw config:</span>
                            <button type="button" className={styles.copyBtn} onClick={copyConfig}>
                                {copied ? <Check size={13} /> : <Copy size={13} />}
                                {copied ? 'Copied' : 'Copy'}
                            </button>
                        </div>
                        <pre className={styles.configPre}>{OPENCLAW_CONFIG_SNIPPET}</pre>
                    </div>
                </main>
            </div>
        </div>
    );
}
